import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const base = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const STATUS_COLORS: Record<string, string> = {
  submitted: '#2563eb',
  acknowledged: '#0284c7',
  assigned: '#4f46e5',
  in_progress: '#d97706',
  awaiting_citizen: '#9333ea',
  resolved: '#16a34a',
  closed: '#6b7280',
};

const fmtHours = (m?: number | string | null) => {
  const minutes = Number(m) || 0;
  if (minutes >= 1440) return `${Math.round(minutes / 1440)} days`;
  return `${Math.round(minutes / 60)} hours`;
};

/**
 * Public transparency page — no login required. Live map of reported
 * issues, ward performance, published SLA promises, outages and self-help.
 */
const PublicServiceStatus: React.FC = () => {
  // Optional /service-status/:code path segment: when present, this page
  // scopes every query to that municipality and shows its real name/logo
  // instead of a hardcoded one. Without a code (the legacy /service-status
  // URL), it falls back to the backend's single default municipality —
  // unchanged behaviour for any existing links/bookmarks.
  const { code } = useParams<{ code?: string }>();
  const [municipality, setMunicipality] = useState<{ id: string; name: string; logoUrl: string | null } | null>(null);
  const [outages, setOutages] = useState<any[]>([]);
  const [articles, setArticles] = useState<any[]>([]);
  const [stats, setStats] = useState<any>();
  const [points, setPoints] = useState<any[]>([]);
  const [promises, setPromises] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map>();

  const search = (municipalityId?: string) =>
    axios
      .get(`${base}/public/knowledge`, { params: { q, municipalityId } })
      .then((r) => setArticles(r.data.data || []));

  // Resolve the municipality from the URL code first (if present), then
  // load every data section scoped to it. Data loads are re-run once the
  // municipality resolves, so a code in the URL never shows another
  // tenant's data even for a moment.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      let municipalityId: string | undefined;
      if (code) {
        try {
          const r = await axios.get(`${base}/public/municipality`, { params: { code } });
          if (!cancelled && r.data?.data) {
            setMunicipality(r.data.data);
            municipalityId = r.data.data.id;
          }
        } catch {
          // Unknown/unreachable code — fall through to the default tenant
          // rather than showing a dead page.
        }
      }
      if (cancelled) return;
      const params = { municipalityId };
      axios.get(`${base}/public/outages`, { params }).then((r) => !cancelled && setOutages(r.data.data || [])).catch(() => {});
      axios.get(`${base}/public/stats`, { params }).then((r) => !cancelled && setStats(r.data.data)).catch(() => {});
      axios.get(`${base}/public/map-points`, { params }).then((r) => !cancelled && setPoints(r.data.data || [])).catch(() => {});
      axios.get(`${base}/public/sla-promises`, { params }).then((r) => !cancelled && setPromises(r.data.data || [])).catch(() => {});
      search(municipalityId);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [code]);

  // Build the map once points arrive.
  useEffect(() => {
    if (!mapRef.current || mapInstance.current || points.length === 0) return;
    const map = L.map(mapRef.current).setView([-29.49, 30.19], 11); // default view; individual points still render at their real locations
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
    }).addTo(map);
    points.forEach((p) => {
      const lat = Number(p.latitude), lng = Number(p.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      L.circleMarker([lat, lng], {
        radius: 7,
        color: STATUS_COLORS[p.status] || '#6b7280',
        fillColor: STATUS_COLORS[p.status] || '#6b7280',
        fillOpacity: 0.7,
        weight: 1.5,
      })
        .bindPopup(
          `<b>${p.category || 'Report'}</b><br/>Status: ${(p.status || '').replaceAll('_', ' ')}<br/>Ward ${p.ward_number ?? '—'} · ${p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}`
        )
        .addTo(map);
    });
    mapInstance.current = map;
    return () => {
      map.remove();
      mapInstance.current = undefined;
    };
  }, [points]);

  const totals = stats?.totals;
  const resolvedRate = totals?.total > 0 ? Math.round((Number(totals.resolved) / Number(totals.total)) * 100) : null;

  const wardRows = useMemo(() => {
    const byWard = new Map<string, { total: number; resolved: number }>();
    (stats?.wards || []).forEach((row: any) => {
      const key = String(row.ward_number ?? '—');
      const entry = byWard.get(key) || { total: 0, resolved: 0 };
      const n = Number(row.count) || 0;
      entry.total += n;
      if (['resolved', 'closed'].includes(row.status)) entry.resolved += n;
      byWard.set(key, entry);
    });
    return Array.from(byWard.entries())
      .filter(([w]) => w !== '—' && w !== 'null')
      .sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [stats]);

  return (
    <main className="min-h-screen bg-slate-50">
      <header className="bg-blue-900 px-6 py-10 text-white">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center gap-3">
            {municipality?.logoUrl && (
              <img src={municipality.logoUrl} alt="" className="h-10 w-10 rounded object-contain bg-white/10" />
            )}
            <h1 className="text-3xl font-bold">{municipality?.name ? `${municipality.name} Service Status` : 'Municipal Service Status'}</h1>
          </div>
          <p className="mt-1 text-blue-100">Live, public view of service delivery — reported issues, our response promises, and current disruptions.</p>
          {totals && (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:max-w-2xl">
              <div><p className="text-3xl font-bold">{totals.total}</p><p className="text-sm text-blue-200">issues reported (90 days)</p></div>
              <div><p className="text-3xl font-bold">{resolvedRate !== null ? `${resolvedRate}%` : '—'}</p><p className="text-sm text-blue-200">resolved</p></div>
              <div><p className="text-3xl font-bold">{totals.avg_hours_to_resolve ? `${totals.avg_hours_to_resolve}h` : '—'}</p><p className="text-sm text-blue-200">average time to resolve</p></div>
            </div>
          )}
        </div>
      </header>

      <div className="mx-auto max-w-6xl space-y-8 p-6">
        {/* Map */}
        {points.length > 0 && (
          <section>
            <h2 className="mb-1 text-xl font-semibold">Reported issues near you</h2>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">Every report from the last 90 days. Colours show current status — green means resolved.</p>
            <div ref={mapRef} className="h-[420px] w-full overflow-hidden rounded-xl border shadow-sm" role="application" aria-label="Map of reported issues" />
          </section>
        )}

        <div className="grid gap-8 md:grid-cols-2">
          {/* SLA promises */}
          <section>
            <h2 className="mb-1 text-xl font-semibold">Our service promises</h2>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">Target times we hold ourselves to for each type of issue.</p>
            {promises.length === 0 ? (
              <p className="rounded bg-white p-4 text-sm text-gray-500 dark:text-gray-400 shadow-sm">Service targets will be published here.</p>
            ) : (
              <div className="space-y-2">
                {promises.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg bg-white p-4 shadow-sm">
                    <div>
                      <p className="font-medium">{p.category || p.name}</p>
                      {p.priority && <p className="text-xs capitalize text-gray-500 dark:text-gray-400">{p.priority} priority</p>}
                    </div>
                    <div className="text-right text-sm">
                      <p><span className="text-gray-500 dark:text-gray-400">Acknowledge:</span> <b>{fmtHours(p.acknowledgement_minutes)}</b></p>
                      <p><span className="text-gray-500 dark:text-gray-400">Resolve:</span> <b>{fmtHours(p.resolution_minutes)}</b></p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Ward performance */}
          <section>
            <h2 className="mb-1 text-xl font-semibold">Performance by ward</h2>
            <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">Reports received and resolved in the last 90 days.</p>
            {wardRows.length === 0 ? (
              <p className="rounded bg-white p-4 text-sm text-gray-500 dark:text-gray-400 shadow-sm">Ward statistics will appear here.</p>
            ) : (
              <div className="overflow-hidden rounded-lg bg-white shadow-sm">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-left"><th className="p-3">Ward</th><th className="p-3">Reported</th><th className="p-3">Resolved</th><th className="p-3">Rate</th></tr></thead>
                  <tbody>
                    {wardRows.map(([ward, v]) => {
                      const rate = v.total ? Math.round((v.resolved / v.total) * 100) : 0;
                      return (
                        <tr key={ward} className="border-t">
                          <td className="p-3 font-medium">Ward {ward}</td>
                          <td className="p-3">{v.total}</td>
                          <td className="p-3">{v.resolved}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100">
                                <div className="h-full rounded-full bg-green-500" style={{ width: `${rate}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">{rate}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Outages */}
          <section>
            <h2 className="mb-3 text-xl font-semibold">Current and planned outages</h2>
            {outages.map((o) => (
              <article key={o.id} className="mb-3 rounded-lg bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between gap-2">
                  <b>{o.title}</b>
                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs uppercase text-orange-700">{o.status}</span>
                </div>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{o.description}</p>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{new Date(o.starts_at).toLocaleString()}{o.estimated_end_at ? ` → est. ${new Date(o.estimated_end_at).toLocaleString()}` : ''}</p>
              </article>
            ))}
            {!outages.length && <p className="rounded-lg bg-green-50 p-4 text-green-800">No public service disruptions are currently listed.</p>}
          </section>

          {/* Knowledge */}
          <section>
            <h2 className="mb-3 text-xl font-semibold">Help and guidance</h2>
            <div className="mb-3 flex">
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search(municipality?.id)}
                className="flex-1 rounded-l-lg border p-2" placeholder="Search help" aria-label="Search help articles" />
              <button onClick={() => search(municipality?.id)} className="rounded-r-lg bg-blue-700 px-4 text-white hover:bg-blue-800">Search</button>
            </div>
            {articles.map((a) => (
              <details key={a.id} className="mb-2 rounded-lg bg-white p-4 shadow-sm">
                <summary className="cursor-pointer font-medium">{a.title}</summary>
                <div className="mt-3 whitespace-pre-wrap text-sm">{a.content}</div>
              </details>
            ))}
          </section>
        </div>

        <footer className="border-t pt-6 pb-2 text-center text-sm text-gray-500 dark:text-gray-400">
          Report an issue with the Municipal Service Delivery Portal app — track progress, get updates, and confirm the fix.
        </footer>
      </div>
    </main>
  );
};

export default PublicServiceStatus;
